import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { RateLimiterService } from '../rate-limiter.service';
import { RateLimitMe } from './model/rate-limit-me';
import { QueryTokenSnapshot } from './query-token-snapshot';

export interface RateLimitDisplayState {
  enabled: boolean;
  availableTokens: number;
  lastRefillTimeMs: number;
  maxBurst: number;
  tokensPerSecond: number;
}

@Injectable({
  providedIn: 'root'
})
export class RateLimitStateService {

  private readonly _state$ = new BehaviorSubject<RateLimitDisplayState>({
    enabled: false,
    availableTokens: 0,
    lastRefillTimeMs: 0,
    maxBurst: 0,
    tokensPerSecond: 0
  });

  public readonly state$ = this._state$.asObservable();

  private loaded = false;

  constructor(private _rateLimiterService: RateLimiterService) { }

  public get snapshot(): RateLimitDisplayState {
    return this._state$.value;
  }

  public get enabled(): boolean {
    return this._state$.value.enabled;
  }

  public ensureLoaded(): Observable<void> {
    if (this.loaded) {
      return of(void 0);
    }
    return this.refresh();
  }

  public refresh(): Observable<void> {
    return this._rateLimiterService.getMe().pipe(
      tap(response => {
        if (response.status !== 'OK' || !response.result) {
          this.applyMe(null);
          return;
        }
        this.applyMe(response.result);
      }),
      map(() => void 0),
      catchError(() => {
        this.applyMe(null);
        return of(void 0);
      })
    );
  }

  public applyFromQueryResult(result: any): void {
    const tokens = this.toSnapshot(result);
    if (!tokens) {
      return;
    }
    const current = this._state$.value;
    this._state$.next({
      enabled: true,
      availableTokens: tokens.tokensRemaining,
      lastRefillTimeMs: Date.now(),
      maxBurst: tokens.maxBurst > 0 ? tokens.maxBurst : current.maxBurst,
      tokensPerSecond: current.tokensPerSecond
    });
    this.loaded = true;
  }

  public projectedEffectiveTokens(): number {
    const state = this._state$.value;
    if (!state.enabled) {
      return 0;
    }
    const elapsedMs = Date.now() - state.lastRefillTimeMs;
    if (elapsedMs <= 0) {
      return Math.round(state.availableTokens);
    }
    const refilled = (elapsedMs / 1000) * state.tokensPerSecond;
    if (refilled <= 0) {
      return Math.round(state.availableTokens);
    }
    return Math.round(Math.min(state.maxBurst, state.availableTokens + refilled));
  }

  public fillPercent(): number {
    const state = this._state$.value;
    if (!state.enabled || state.maxBurst <= 0) {
      return 0;
    }
    return Math.min(100, Math.round((this.projectedEffectiveTokens() / state.maxBurst) * 100));
  }

  private applyMe(me: RateLimitMe | null): void {
    if (!me || !me.enabled) {
      this._state$.next({
        enabled: false,
        availableTokens: 0,
        lastRefillTimeMs: 0,
        maxBurst: 0,
        tokensPerSecond: 0
      });
      this.loaded = true;
      return;
    }
    this._state$.next({
      enabled: true,
      availableTokens: Math.round(me.availableTokens),
      lastRefillTimeMs: me.lastRefillTimeMs,
      maxBurst: Math.round(me.maxBurst),
      tokensPerSecond: Math.round(me.tokensPerSecond)
    });
    this.loaded = true;
  }

  private toSnapshot(result: any): QueryTokenSnapshot | null {
    if (!result || !result.rateLimitEnabled) {
      return null;
    }
    return {
      rateLimitEnabled: true,
      usedTokensNumber: Math.round(result.usedTokensNumber || 0),
      tokensRemaining: Math.round(result.tokensRemaining || 0),
      maxBurst: Math.round(result.maxBurst || 0)
    };
  }
}
